import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Public client
export const apiClient = axios.create({ baseURL: API });

// Admin client — sends httpOnly cookies
export const adminClient = axios.create({ baseURL: `${API}/admin`, withCredentials: true });

export const searchClient = axios.create({ baseURL: API, withCredentials: true });

// ---- Public ----
export const getBooks = (params = {}) => apiClient.get("/books", { params }).then(r => r.data);
export const getBook = (slug) => apiClient.get(`/books/${slug}`).then(r => r.data);
export const submitContact = (payload) => apiClient.post("/contact", payload).then(r => r.data);
export const subscribeNewsletter = (payload) => apiClient.post("/newsletter", payload).then(r => r.data);
export const applyMission = (payload) => apiClient.post("/mission/apply", payload).then(r => r.data);
export const globalSearch = (params) => searchClient.get("/search", { params }).then(r => r.data);
export const suggestSearch = (q) => searchClient.get("/suggest", { params: { q } }).then(r => r.data);

// ---- Admin auth ----
export const adminLogin = (email, password) => adminClient.post("/auth/login", { email, password }).then(r => r.data);
export const adminLogout = () => adminClient.post("/auth/logout").then(r => r.data);
export const adminMe = () => adminClient.get("/auth/me").then(r => r.data);
export const adminForgot = (email) => adminClient.post("/auth/forgot-password", { email }).then(r => r.data);
export const adminReset = (token, new_password) => adminClient.post("/auth/reset-password", { token, new_password }).then(r => r.data);

// ---- Admin books ----
export const listAdminBooks = (params = {}) => adminClient.get("/books", { params }).then(r => r.data);
export const getAdminBook = (id) => adminClient.get(`/books/${id}`).then(r => r.data);
export const createBook = (payload) => adminClient.post("/books", payload).then(r => r.data);
export const updateBook = (id, payload) => adminClient.put(`/books/${id}`, payload).then(r => r.data);
export const publishBook = (id) => adminClient.post(`/books/${id}/publish`).then(r => r.data);
export const unpublishBook = (id) => adminClient.post(`/books/${id}/unpublish`).then(r => r.data);
export const archiveBook = (id) => adminClient.post(`/books/${id}/archive`).then(r => r.data);
export const restoreBook = (id) => adminClient.post(`/books/${id}/restore`).then(r => r.data);
export const exportBooks = () => adminClient.get("/books-export.json").then(r => r.data);

// ---- Admin generic content ----
export const listContent = (ctype) => adminClient.get(`/content/${ctype}`).then(r => r.data);
export const getContent = (ctype, id) => adminClient.get(`/content/${ctype}/${id}`).then(r => r.data);
export const createContent = (ctype, payload) => adminClient.post(`/content/${ctype}`, payload).then(r => r.data);
export const updateContent = (ctype, id, payload) => adminClient.put(`/content/${ctype}/${id}`, payload).then(r => r.data);
export const publishContent = (ctype, id) => adminClient.post(`/content/${ctype}/${id}/publish`).then(r => r.data);
export const archiveContent = (ctype, id) => adminClient.post(`/content/${ctype}/${id}/archive`).then(r => r.data);
export const restoreContent = (ctype, id) => adminClient.post(`/content/${ctype}/${id}/restore`).then(r => r.data);

// ---- Admin media ----
export const listMedia = (params = {}) => adminClient.get("/media", { params }).then(r => r.data);
export const uploadMedia = (file, { kind = "other", alt_text = "", caption = "" } = {}) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  fd.append("alt_text", alt_text);
  fd.append("caption", caption);
  return adminClient.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const replaceMedia = (id, { file, alt_text, caption, kind }) => {
  const fd = new FormData();
  if (file) fd.append("file", file);
  if (alt_text !== undefined) fd.append("alt_text", alt_text);
  if (caption !== undefined) fd.append("caption", caption);
  if (kind !== undefined) fd.append("kind", kind);
  return adminClient.put(`/media/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const deleteMedia = (id) => adminClient.delete(`/media/${id}`).then(r => r.data);

// ---- Admin staff ----
export const listUsers = () => adminClient.get("/users").then(r => r.data);
export const createUser = (payload) => adminClient.post("/users", payload).then(r => r.data);
export const updateUser = (id, payload) => adminClient.patch(`/users/${id}`, payload).then(r => r.data);

// ---- Admin activity ----
export const getActivity = (limit = 100) => adminClient.get("/activity", { params: { limit } }).then(r => r.data);

// ---- Admin homepage/settings ----
export const getHomepage = () => adminClient.get("/homepage").then(r => r.data);
export const saveHomepage = (payload) => adminClient.put("/homepage", payload).then(r => r.data);
export const getSetting = (key) => adminClient.get(`/settings/${key}`).then(r => r.data);
export const saveSetting = (key, payload) => adminClient.put(`/settings/${key}`, payload).then(r => r.data);

// ---- Admin translations ----
export const listTranslations = () => adminClient.get("/translations").then(r => r.data);
export const saveTranslation = (key, payload) => adminClient.put(`/translations/${key}`, payload).then(r => r.data);

// ---- Submissions ----
export const listSubmissions = (stype, params = {}) => adminClient.get(`/submissions/${stype}`, { params }).then(r => r.data);
export const updateSubmission = (stype, id, payload) => adminClient.patch(`/submissions/${stype}/${id}`, payload).then(r => r.data);
export const submissionsCsvUrl = (stype) => `${API}/admin/submissions/${stype}/export.csv`;

// ---- Reservations ----
export const submitReservation = (payload) => apiClient.post("/reservations", payload).then(r => r.data);
export const listReservations = (params = {}) => adminClient.get("/reservations", { params }).then(r => r.data);
export const getReservation = (id) => adminClient.get(`/reservations/${id}`).then(r => r.data);
export const updateReservation = (id, payload) => adminClient.patch(`/reservations/${id}`, payload).then(r => r.data);
export const deleteReservation = (id) => adminClient.delete(`/reservations/${id}`).then(r => r.data);
export const reservationsCsvUrl = (params = {}) => {
  const qs = new URLSearchParams(Object.entries(params).filter(([_, v]) => v)).toString();
  return `${API}/admin/reservations/export.csv${qs ? `?${qs}` : ""}`;
};

// Helper to convert media URL from backend
export const mediaUrl = (path) => path?.startsWith("http") ? path : `${BACKEND_URL}${path}`;
